import React, { Component } from "react";
import { Link } from "react-router-dom";

// CSS
import "../../styles/newDesign.scss";
import "./Courses.scss";

// Components
import Header from "../../components/Header/Header";
import Footer from "../../components/Footer/Footer";
import Icon from "../../components/Icon";

// Helper
import { resolveTenant, listCourseCatalog, requestSubscription, isLoggedIn, cachedTenant } from "../../Helper/Api";
import { brandingOf } from "../../Helper/Branding";
import { t } from "../../Helper/i18n";
import Toast from "../../components/Toast/Toast";

const properties = require("../../properties.json");

class Courses extends Component {
  state = {
    tenant: cachedTenant(),
    notFound: false,
    courses: [],
    subscribing: null, // course id currently being requested
    msg: null,
    // The catalogue arrives after the tenant does. Without this the page
    // showed its "no courses" state for a moment on every visit.
    loading: true,
  };

  componentDidMount() {
    resolveTenant()
      .then((tenant) => {
        document.title = tenant.name ? `${t("courses")} | ${tenant.name}` : properties.site_name;
        this.setState({ tenant });
        return listCourseCatalog(tenant.teacherUserId);
      })
      .then((courses) => this.setState({ courses: courses || [], loading: false }))
      .catch(() => this.setState({ notFound: true, loading: false }));
  }

  componentWillUnmount() {
    if (this.io) this.io.disconnect();
  }

  /* Reveal as each card comes into view. Nothing is hidden until this has run
     and put `js-reveal` on the grid, so a page whose script never arrives
     still shows every course. */
  watchReveal = (root) => {
    if (!root || this.io) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches || !("IntersectionObserver" in window)) return;

    root.classList.add("js-reveal");
    this.io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-in");
          this.io.unobserve(entry.target);
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.12 }
    );
    root.querySelectorAll("[data-reveal]").forEach((el) => this.io.observe(el));
  };

  subscribe = (course) => {
    if (!isLoggedIn()) {
      window.location.href = "/sign-in";
      return;
    }
    this.setState({ subscribing: course.id, msg: null });
    requestSubscription({ periodId: course.periodId, branchId: this.state.tenant.branchId })
      .then(() => {
        this.setState({ subscribing: null, msg: { text: t("subSent"), type: "success" } });
      })
      .catch((error) => {
        this.setState({ subscribing: null, msg: { text: error.message || t("subFailed"), type: "warning" } });
      });
  };

  render() {
    const { tenant, notFound, courses, subscribing, msg, loading } = this.state;

    if (notFound) {
      return (
        <div className="page">
          <div className="container text-center">
            <p>{t("notFound")}</p>
          </div>
        </div>
      );
    }

    if (!tenant) return null;

    const teacherName = tenant.name || "";
    const b = brandingOf(tenant);

    return (
      <>
        {/* The band carries the header, exactly as the Home hero does, so the
            island floats on one continuous ground rather than two. */}
        <div className="cx-stage mu mu-ground">
          <div className="cx-sky" aria-hidden="true">
            <span className="aur a1"></span>
            <span className="aur a2"></span>
            <span className="dots"></span>
          </div>

          <Header tenant={tenant} onStage />

          <div className="cx-head">
            <span className="mu-chip"><Icon name="book" className="chipi" /> {t("ourCourses")}</span>
            <h1>{t("learnWith", { name: teacherName })}</h1>
            <p>{b.tagline ? b.tagline : t("coursesLead")}</p>
            {!loading && courses.length > 0 && (
              <span className="cx-count">
                {courses.length === 1 ? t("coursesCountOne") : t("coursesCount", { n: courses.length })}
              </span>
            )}
          </div>
        </div>

        <div className="cx-body mu">
          <Toast msg={msg} />

          {loading ? (
            <div className="cx-grid">
              {[0, 1, 2].map((i) => (
                <div className="cx-card is-wait" key={i} aria-hidden="true">
                  <span className="cx-thumb"></span>
                  <span className="cx-skel w70"></span>
                  <span className="cx-skel w90"></span>
                  <span className="cx-skel w40"></span>
                </div>
              ))}
            </div>
          ) : courses.length === 0 ? (
            <div className="cx-empty">
              <span className="ic" aria-hidden="true"><Icon name="book" /></span>
              <b>{t("noCoursesYet")}</b>
              <p>{t("noCoursesText")}</p>
              <Link to="/contact" className="mu-btn is-gold has-arw">
                {t("contactName", { name: teacherName })} <Icon name="arrow" className="arw" />
              </Link>
            </div>
          ) : (
            <div className="cx-grid" ref={this.watchReveal}>
              {courses.map((course, i) => {
                const cardImage = course.listImage || course.image;
                return (
                  <article className="cx-card" key={course.id} data-reveal style={{ "--i": i % 3 }}>
                    <a
                      href={`/courses/${course.id}`}
                      className={`cx-thumb ${cardImage ? "" : "is-blank"}`}
                      style={cardImage ? { backgroundImage: `url(${cardImage})` } : undefined}
                      aria-label={course.title}
                    >
                      {!cardImage && <span className="mark" aria-hidden="true"><Icon name="book" /></span>}
                      {course.showOnHome && <span className="cx-flag">{t("featured")}</span>}
                      <span className="cx-open" aria-hidden="true"><Icon name="arrow" /></span>
                    </a>

                    <div className="cx-cardbody">
                      <a href={`/courses/${course.id}`} className="cx-title"><h3>{course.title}</h3></a>
                      {course.description && <p>{course.description}</p>}

                      <div className="cx-acts">
                        <button
                          type="button"
                          className={`mu-btn is-gold is-sm ${subscribing === course.id ? "is-busy" : ""}`}
                          disabled={subscribing === course.id}
                          onClick={() => this.subscribe(course)}
                        >
                          {subscribing === course.id ? t("sending") : t("requestSub")}
                        </button>
                        <a href={`/courses/${course.id}`} className="cx-more">
                          {t("details")} <Icon name="arrow" />
                        </a>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>

        <Footer tenant={tenant} />
      </>
    );
  }
}

export default Courses;
